import { createDecorator } from '../resolvers/definition/decorator';
import { ResolverRuntime } from '../resolvers/definition/resolver';
import { PersistenceManager } from './persistenceManager';
import {
  toError,
  toLoading,
  toSuccess,
} from '../resolvers/definition/resolverState';
import { PersistConfig } from './types';

export const createPersistenceDecorator = (
  persistenceManager: NonNullable<PersistenceManager>,
  { maxStoredAge = 0, maxFallbackAge = maxStoredAge }: PersistConfig
) =>
  createDecorator('persistence', (attach) => (params, runtime) => {
    if (runtime.getState()?.data !== undefined) {
      return attach(params, runtime);
    }

    const instanceKey = runtime.env.instanceKey;

    // SWR
    // If the resolver hasn't yet pushed data and the IDB read resolves with,
    // data that's fresh enough, emit a stale loading state with the IDB's data.
    let resolved = false;

    const idbRead = persistenceManager
      .read(instanceKey)
      .then((entry) => {
        if (!resolved && entry && entry.storedAt + maxStoredAge > Date.now()) {
          runtime.next(toLoading()(toSuccess(JSON.parse(entry.data))()));
        }

        return entry;
      })
      .catch((err) => {
        runtime.env.logger?.error('persistence hydration failed', {
          instanceKey,
          error: (err as Error).message,
        });
      });

    // Fault tolerance
    // Defer error states until we have a result from IDB.
    // Once we do, publish the deferred state w/ the IDB results if fresh enough.
    let deferredError: { state: any; meta: any } | null = null;

    const wrappedNext: ResolverRuntime<any, any>['next'] = (state, meta) => {
      // If a data-ful state is published, the resolver has resolved and we should never
      // publish from IDB.
      if (state.data !== undefined) {
        resolved = true;
      }

      // If the resolver has previously pushed data, or we aren't in an error state,
      // clear the pending error and publish the state.
      if (resolved || !state.error) {
        deferredError = null;
        runtime.next(state, meta);
        return;
      }

      // Always capture the new state, but only fire the IDB read on the first deferral
      const isFirstDeferral = deferredError == null;
      deferredError = { state, meta };

      if (isFirstDeferral) {
        idbRead
          .then((entry) => {
            // If the deferred error was cleared before we got here,
            // don't publish anything.
            if (!deferredError) {
              return;
            }

            const pendingError = deferredError;

            // If the IDB data is fresh enough, publish the error with the cached data.
            if (entry && entry.storedAt + maxFallbackAge > Date.now()) {
              let cachedData: any;

              try {
                cachedData = JSON.parse(entry.data);
              } catch (err) {
                runtime.env.logger?.error('persistence fallback failed', {
                  instanceKey,
                  error: (err as Error).message,
                });
                runtime.next(pendingError.state, pendingError.meta);
                deferredError = null;
                return;
              }

              runtime.next(
                toError(pendingError.state.error)(toSuccess(cachedData)()),
                pendingError.meta
              );
            } else {
              // If the IDB data is stale, publish the deferred error.
              runtime.next(pendingError.state, pendingError.meta);
            }

            // Clear the deferred error so we don't publish it again.
            deferredError = null;
          })
          .catch(() => {});
      }
    };

    return attach(params, {
      ...runtime,
      next: wrappedNext,
      // Return the deferred error state if it exists, otherwise return the current state.
      // This lets the resolver read its own writes
      getState: () => deferredError?.state ?? runtime.getState(),
    });
  });
